/* ---------------------------------------------------------------------------
   STL 2026 — 4.7  ONE drawer, content swapped in place

   Serena, 2 Sep 2026: the row list inside an open drawer is per the Figma, but the
   INTERACTION should follow cos.com — picking another section swaps the content
   without closing and reopening. Verified on their PDP: the open dialog count
   stays at 1 across a switch, `aria-selected` moves, and the panel never animates
   out. The previous build closed one `modal-dialog` and opened another, which
   reads as a flicker and loses scroll position.

   WHY THE SECTIONS ARE HARVESTED FROM THE DOM rather than authored here. Each
   drawer's content comes from a different place — `product.description`, two
   metafields, and a native `popup` block whose copy is a rich-text field in the
   theme editor. Letting each block render its own dialog and then folding them
   into one keeps every source intact, and in particular keeps Shipping & Returns
   editable in the editor. Adding a drawer needs no change to this file.

   Progressive enhancement is not a concern: Madrid marks every opener
   `no-js-hidden`, so the drawers do not exist without JS at all.
   ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var PANEL = '.product__info-main_content';
  var DIALOG_ID = 'PopupModal-stl-drawers';

  var sections = [];
  var dialog = null;
  var titleEl = null;
  var bodyEl = null;
  var navEl = null;

  function harvest() {
    var root = document.querySelector(PANEL);
    if (!root) return [];

    return Array.prototype.slice
      .call(root.querySelectorAll('.product-popup'))
      .map(function (wrap) {
        var opener = wrap.querySelector('modal-opener[data-modal]');
        var button = wrap.querySelector('button');
        if (!opener || !button) return null;

        var source = document.querySelector(opener.getAttribute('data-modal'));
        if (!source) return null;

        var info = source.querySelector('.product-popup-modal__content-info');
        var heading = source.querySelector('.product-popup-modal__title');

        return {
          label: (heading ? heading.textContent : button.textContent).trim(),
          html: info ? info.innerHTML : '',
          button: button,
          opener: opener,
          source: source
        };
      })
      .filter(Boolean);
  }

  function build() {
    if (document.getElementById(DIALOG_ID)) return;

    sections = harvest();
    if (!sections.length) return;

    /* Clone Madrid's own close button so the icon and its classes stay in step
       with the theme rather than being reproduced here. */
    var closeSource = sections[0].source.querySelector('.product-popup-modal__close');

    dialog = document.createElement('modal-dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'main-product-popup product-popup-modal';
    dialog.innerHTML =
      '<div role="dialog" aria-modal="true" class="product-popup-modal__content" tabindex="-1">' +
      '<div class="product-popup-modal__top">' +
      '<h3 class="product-popup-modal__title"></h3>' +
      '</div>' +
      '<div class="product-popup-modal__content-info">' +
      '<div class="product-popup-modal__text"></div>' +
      '</div>' +
      '<nav class="stl-drawer-links" aria-label="Other product information"></nav>' +
      '</div>';

    var top = dialog.querySelector('.product-popup-modal__top');
    if (closeSource) top.appendChild(closeSource.cloneNode(true));

    document.body.appendChild(dialog);

    /* CLOSE BUTTON — `ModalDialog` binds it in its CONSTRUCTOR, via
       `init()` → `this.querySelector('[id^="ModalClose-"]')`. `createElement`
       upgrades the element immediately, so that ran a few lines above while the
       dialog was still EMPTY: it found nothing and bound nothing, and the close
       button appended afterwards is an inert clone. The X was visible, hit-
       testable and completely dead.

       Madrid exposes `reinit()` for exactly this, so re-running it once the
       content is in place needs no core edit. ESC and the backdrop click were
       never affected — the constructor binds those to the element itself. */
    if (typeof dialog.reinit === 'function') dialog.reinit();

    titleEl = dialog.querySelector('.product-popup-modal__title');
    bodyEl = dialog.querySelector('.product-popup-modal__text');
    navEl = dialog.querySelector('.stl-drawer-links');

    sections.forEach(function (section, index) {
      /* Point every trigger at the single dialog. `ModalOpener` reads `data-modal`
         at CLICK time, not at construction, so repointing it after the custom
         element has upgraded is enough. */
      section.opener.setAttribute('data-modal', '#' + DIALOG_ID);

      /* Capture phase, so the section is set BEFORE Madrid's own click handler
         shows the dialog — otherwise the drawer opens on the previous section
         and visibly swaps a frame later. */
      section.button.addEventListener(
        'click',
        function () {
          setSection(index);
        },
        true
      );

      /* The per-block dialog has done its job as a content carrier. */
      if (section.source.parentNode) {
        section.source.parentNode.removeChild(section.source);
      }
    });

    setSection(0);
  }

  function setSection(index) {
    var section = sections[index];
    if (!section) return;

    titleEl.textContent = section.label;
    bodyEl.innerHTML = section.html;
    dialog
      .querySelector('[role="dialog"]')
      .setAttribute('aria-label', section.label);

    navEl.innerHTML = '';
    sections.forEach(function (other, otherIndex) {
      if (otherIndex === index) return;

      var row = document.createElement('button');
      row.type = 'button';
      row.className = 'stl-drawer-links__row';
      row.textContent = other.label;
      row.addEventListener('click', function () {
        setSection(otherIndex);
        /* Content only — the dialog stays open, which is the whole point. */
      });
      navEl.appendChild(row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }

  document.addEventListener('shopify:section:load', build);
})();
